import type { ReactNode } from 'react'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Pagination } from '@/components/ui/pagination-controls'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

export interface PosColumn<T> {
  label: string
  render: (row: T) => ReactNode
  className?: string
}

interface Props<T extends { _id: string }> {
  columns: PosColumn<T>[]
  rows: T[]
  loading: boolean
  empty: string
  filters?: ReactNode
  page?: number
  count?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  onRowClick?: (row: T) => void
  footer?: ReactNode
}

/** A read-only POS history / report table with optional filters and paging. */
export function PosListCard<T extends { _id: string }>({ columns, rows, loading, empty, filters, page, count, pageSize, onPageChange, onRowClick, footer }: Props<T>) {
  return (
    <Card>
      {filters && (
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">{filters}</div>
        </CardHeader>
      )}
      <CardContent className={filters ? undefined : 'pt-4'}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.label} className={c.className}>{c.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i}>
                    {columns.map((c) => (
                      <TableCell key={c.label} className={c.className}><Skeleton className="h-4 w-3/4" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="py-10 text-center text-sm text-muted-foreground">{empty}</TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row._id} onClick={onRowClick ? () => onRowClick(row) : undefined} className={onRowClick ? 'cursor-pointer' : undefined}>
                    {columns.map((c) => (
                      <TableCell key={c.label} className={c.className}>{c.render(row)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
            {footer}
          </Table>
        </div>
        {onPageChange && page && pageSize && (
          <Pagination.Buttons currentPage={page} totalItems={count ?? 0} pageSize={pageSize} onPageChange={onPageChange} />
        )}
      </CardContent>
    </Card>
  )
}
